import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, MessageCircle, X } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import useAccess from '../../hooks/useAccess';
import { API_ROOT } from '../../config/api';
import { formatDateInput, timeToMinutes } from './utils';
import {
    clientMessage, openWhatsApp, prettyDate, prettyTime, staffDayScheduleMessage, staffNewBookingMessage, staffPhoneOf,
} from '../../utils/appointmentMessages';

import AppointmentHeader from './components/AppointmentHeader/AppointmentHeader';
import AppointmentToolbar from './components/AppointmentToolbar/AppointmentToolbar';
import AppointmentCalendar from './components/AppointmentCalendar/AppointmentCalendar';
import AppointmentDrawer from './components/AppointmentDrawer/AppointmentDrawer';

import styles from './AppointmentBooking.module.css';
import ex from './AppointmentExtras.module.css';

const SM_API = `${API_ROOT}/api/v1/smiify/sm`;

export const STATUS_META = {
    Confirmed: { label: 'Confirmed', color: 'var(--h-color-primary-600, #673de6)' },
    Waiting: { label: 'Waiting', color: 'var(--h-color-neutral-500, #797980)' },
    In_Service: { label: 'In service', color: 'var(--danger--500, #fc5185)' },
    Completed: { label: 'Completed', color: 'var(--h-color-success-600, #007a49)' },
    Cancelled: { label: 'Cancelled', color: 'var(--h-color-danger-600, #be1025)' },
    No_Show: { label: 'No show', color: 'var(--h-color-neutral-700, #36363b)' },
};

const minutesToTime = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function AppointmentBooking() {
    const { activeStore } = useStore();
    const { user } = useAuth();
    const { can } = useAccess();
    const token = localStorage.getItem('smiify_token');
    const storeId = activeStore?._id || activeStore?.id;

    // Live access (the server checks the same rules)
    const canCreate = can('appointments.create');
    const canEdit = can('appointments.edit');
    const canReschedule = can('appointments.reschedule');
    const canChangeStatus = can('appointments.change_status');
    const canCancel = can('appointments.cancel_booking');
    const canWhatsApp = can('appointments.send_whatsapp');
    const canStaffMsg = can('appointments.send_staff_schedule');
    const viewAllStaff = can('appointments.view_all_staff');
    const canOpen = canEdit || canChangeStatus || canCancel || canWhatsApp;

    const today = formatDateInput(new Date());
    const [selectedDate, setSelectedDate] = useState(today);

    const [appointments, setAppointments] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [serviceList, setServiceList] = useState([]);
    const [isFetching, setIsFetching] = useState(true);

    const [search, setSearch] = useState('');
    const [staffFilter, setStaffFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [toast, setToast] = useState(null);       // { type, text }
    const [shareBar, setShareBar] = useState(null); // { appointment, kind }

    const initialFormState = { id: '', date: today, start: '09:00', end: '09:30', staffId: '', serviceId: '', clientId: '', clientName: '', phone: '', status: 'Confirmed', notes: '', amount: 0, serviceName: '' };
    const [formData, setFormData] = useState(initialFormState);

    const [clientResults, setClientResults] = useState([]);
    const [showClientDropdown, setShowClientDropdown] = useState(false);

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, 'x-store-id': storeId }), [token, storeId]);
    const jsonHeaders = useMemo(() => ({ ...headers, 'Content-Type': 'application/json' }), [headers]);

    const flash = (type, text) => {
        setToast({ type, text });
        setTimeout(() => setToast(null), type === 'ok' ? 3500 : 6000);
    };

    /* ----------------------------- data ----------------------------- */
    const fetchAppointments = useCallback(async (dateStr) => {
        if (!storeId || storeId === 'global_hq') return;
        try {
            const res = await fetch(`${SM_API}/appointments?date=${dateStr}`, { headers });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) setAppointments(data.data || []);
            else flash('error', data.message || 'Could not load appointments.');
        } catch {
            flash('error', 'Cannot reach the server. Try again in a minute.');
        }
    }, [storeId, headers]);

    const fetchMasterData = useCallback(async () => {
        if (!storeId || storeId === 'global_hq') {
            setIsFetching(false); setStaffList([]); setServiceList([]); setAppointments([]);
            return;
        }
        setIsFetching(true);
        try {
            const [staffRes, serviceRes] = await Promise.all([
                fetch(`${SM_API}/staff`, { headers }),
                fetch(`${SM_API}/services`, { headers }),
            ]);
            const staffData = await staffRes.json().catch(() => ({}));
            const serviceData = await serviceRes.json().catch(() => ({}));
            if (staffData.success) setStaffList((staffData.data || []).filter((s) => s.isActive !== false && s.role !== 'ADMIN'));
            if (serviceData.success) setServiceList(serviceData.data || []);
            await fetchAppointments(selectedDate);
        } catch {
            flash('error', 'Cannot reach the server. Try again in a minute.');
        } finally {
            setIsFetching(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, headers, fetchAppointments]);

    useEffect(() => { fetchMasterData(); }, [fetchMasterData]);
    useEffect(() => { if (storeId && storeId !== 'global_hq') fetchAppointments(selectedDate); }, [selectedDate, storeId, fetchAppointments]);

    const changeDate = (days) => setSelectedDate((prev) => {
        const [y, m, d] = prev.split('-').map(Number);
        return formatDateInput(new Date(y, m - 1, d + days));
    });

    /* ----------------------------- own calendar only ----------------------------- */
    // Without "see every staff calendar": only the staff profile linked to this login
    const ownStaff = useMemo(() => {
        const me = String(user?.id || user?._id || '');
        return staffList.find((s) => String(s.loginAccountId?._id || s.loginAccountId || s.systemAccessProtocol?.loginAccountId || s.userId || '') === me) || null;
    }, [staffList, user]);
    const visibleStaff = useMemo(() => (viewAllStaff ? staffList : (ownStaff ? [ownStaff] : [])), [viewAllStaff, staffList, ownStaff]);

    /* ----------------------------- helpers ----------------------------- */
    const staffById = useMemo(() => new Map(staffList.map((s) => [String(s._id), s])), [staffList]);
    const staffNameOf = (apt) => apt?.staffName || staffById.get(String(apt?.staffId?._id || apt?.staffId))?.name || '';
    const staffPhoneFor = (apt) => apt?.staffPhone || staffPhoneOf(staffById.get(String(apt?.staffId?._id || apt?.staffId)));

    const replaceAppointment = (apt) => setAppointments((list) => {
        const exists = list.some((a) => a._id === apt._id);
        const next = exists ? list.map((a) => (a._id === apt._id ? apt : a)) : [...list, apt];
        return next.filter((a) => a.date === selectedDate);
    });

    /** Open WhatsApp with the message, and save in the appointment that it was sent */
    const sendWhatsApp = useCallback(async ({ appointment, type, audience }) => {
        const enriched = { ...appointment, staffName: staffNameOf(appointment) };
        const isClient = audience === 'CLIENT';
        const phone = isClient ? appointment.clientPhone : staffPhoneFor(appointment);
        const text = isClient
            ? clientMessage(type, enriched, activeStore)
            : staffNewBookingMessage(enriched, activeStore, enriched.staffName);

        if (!isClient && !phone) flash('ok', 'This staff member has no phone saved. WhatsApp will ask you to pick the contact.');
        openWhatsApp(phone, text);

        try {
            const res = await fetch(`${SM_API}/appointments/${appointment._id}/notifications`, {
                method: 'POST', headers: jsonHeaders, body: JSON.stringify({ type: isClient ? type : 'STAFF_NEW', audience, to: phone }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) {
                setAppointments((list) => list.map((a) => (a._id === appointment._id ? { ...a, notifications: data.notifications, lastNotification: data.data } : a)));
            }
        } catch { /* message already opened; logging is best effort */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore, jsonHeaders, staffById]);

    /** Staff member's full day on WhatsApp */
    const shareStaffSchedule = (staff) => {
        const list = appointments.filter((a) => String(a.staffId?._id || a.staffId) === String(staff._id));
        openWhatsApp(staffPhoneOf(staff), staffDayScheduleMessage(list, activeStore, staff.name, selectedDate));
    };

    /* ----------------------------- client search ----------------------------- */
    const handleClientSearchInput = async (e) => {
        const query = e.target.value;
        setFormData((prev) => ({ ...prev, clientName: query, clientId: '' }));
        if (query.length < 3) { setClientResults([]); setShowClientDropdown(false); return; }
        try {
            const res = await fetch(`${SM_API}/customers/search?query=${encodeURIComponent(query)}`, { headers });
            const data = await res.json().catch(() => ({}));
            if (data.success && data.data?.length) { setClientResults(data.data); setShowClientDropdown(true); } else setShowClientDropdown(false);
        } catch { /* ignore */ }
    };

    const selectClient = (client) => {
        setFormData((prev) => ({ ...prev, clientId: client._id, clientName: client.name, phone: client.phone }));
        setShowClientDropdown(false);
    };

    /* ----------------------------- drawer ----------------------------- */
    const openNewAppointment = (staffId = '', time = '09:00') => {
        if (!canCreate) return;
        const start = timeToMinutes(time);
        setFormData({ ...initialFormState, date: selectedDate, start: time, end: minutesToTime(start + 30), staffId });
        setErrorMsg('');
        setDrawerOpen(true);
    };

    const openEditAppointment = (apt, e) => {
        if (e) e.stopPropagation();
        if (!canOpen) return;
        setFormData({
            id: apt._id, date: apt.date, start: apt.startTime, end: apt.endTime,
            staffId: apt.staffId?._id || apt.staffId, serviceId: apt.serviceId?._id || apt.serviceId,
            clientId: apt.clientId?._id || apt.clientId || '', clientName: apt.clientName,
            phone: apt.clientPhone, status: apt.status, notes: apt.notes || '',
            amount: apt.amount || 0, serviceName: apt.serviceSnapshot?.name || apt.serviceName || '',
        });
        setErrorMsg('');
        setDrawerOpen(true);
    };

    const handleServiceChange = (serviceId) => {
        const srv = serviceList.find((s) => s._id === serviceId);
        if (!srv) { setFormData((prev) => ({ ...prev, serviceId: '', amount: 0, serviceName: '' })); return; }
        setFormData((prev) => ({
            ...prev, serviceId, serviceName: srv.name,
            amount: srv.effectivePrice || srv.price || 0,
            end: minutesToTime(timeToMinutes(prev.start) + (Number(srv.duration) || 30)),
        }));
    };

    // Keep the same length when the start time changes
    const handleStartChange = (start) => setFormData((prev) => {
        const length = Math.max(5, timeToMinutes(prev.end) - timeToMinutes(prev.start) || 30);
        return { ...prev, start, end: minutesToTime(Math.min(24 * 60, timeToMinutes(start) + length)) };
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');
        if (!formData.clientName || !formData.phone || !formData.staffId || !formData.serviceId) { setErrorMsg('Fill client, mobile, service and staff.'); return; }
        if (String(formData.phone).replace(/\D/g, '').length !== 10) { setErrorMsg('Enter a 10-digit mobile number.'); return; }
        if (formData.id && !canEdit) { setErrorMsg('You do not have access to edit appointments.'); return; }
        if (formData.id && !canReschedule && currentAppointment) {
            const moved = formData.date !== currentAppointment.date || formData.start !== currentAppointment.startTime
                || formData.end !== currentAppointment.endTime || String(formData.staffId) !== String(currentAppointment.staffId?._id || currentAppointment.staffId);
            if (moved) { setErrorMsg('You do not have access to change the time or staff of a booking.'); return; }
        }

        setIsSubmitting(true);
        try {
            const payload = {
                storeId, date: formData.date, startTime: formData.start, endTime: formData.end,
                serviceDuration: timeToMinutes(formData.end) - timeToMinutes(formData.start),
                staffId: formData.staffId, serviceId: formData.serviceId, serviceName: formData.serviceName,
                clientId: formData.clientId || null, clientName: formData.clientName, clientPhone: formData.phone,
                amount: formData.amount, status: formData.status, notes: formData.notes,
            };
            const isEdit = Boolean(formData.id);
            const res = await fetch(isEdit ? `${SM_API}/appointments/${formData.id}` : `${SM_API}/appointments`, {
                method: isEdit ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) { setErrorMsg(data.message || 'Could not save.'); return; }

            setDrawerOpen(false);
            if (data.data?.date === selectedDate) replaceAppointment(data.data);
            else { setSelectedDate(data.data?.date || selectedDate); }
            setShareBar({ appointment: data.data, kind: !isEdit ? 'NEW' : data.rescheduled ? 'RESCHEDULED' : 'UPDATED' });
        } catch {
            setErrorMsg('Cannot reach the server.');
        } finally {
            setIsSubmitting(false);
        }
    };

    /** Quick status change (from the drawer) */
    const handleQuickStatus = async (status) => {
        if (!formData.id) { setFormData((prev) => ({ ...prev, status })); return; }
        if (status === 'Cancelled' ? !canCancel : !canChangeStatus) { setErrorMsg('You do not have access to change this status.'); return; }
        setIsSubmitting(true);
        try {
            const res = await fetch(`${SM_API}/appointments/${formData.id}/status`, { method: 'PATCH', headers: jsonHeaders, body: JSON.stringify({ status }) });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) { setErrorMsg(data.message || 'Could not change the status.'); return; }
            replaceAppointment(data.data);
            setFormData((prev) => ({ ...prev, status }));
            if (status === 'Cancelled') { setDrawerOpen(false); setShareBar({ appointment: data.data, kind: 'CANCELLED' }); }
            else if (status === 'Completed') { setDrawerOpen(false); setShareBar({ appointment: data.data, kind: 'COMPLETED' }); }
            else flash('ok', data.message);
        } catch {
            setErrorMsg('Cannot reach the server.');
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ----------------------------- filters & summary ----------------------------- */
    const visibleAppointments = useMemo(() => appointments.filter((apt) => {
        const q = search.trim().toLowerCase();
        const matchesSearch = !q || apt.clientName?.toLowerCase().includes(q) || apt.clientPhone?.includes(q) || String(apt.serviceName || '').toLowerCase().includes(q);
        const aptStaff = String(apt.staffId?._id || apt.staffId);
        if (!viewAllStaff && (!ownStaff || aptStaff !== String(ownStaff._id))) return false;
        const matchesStaff = staffFilter === 'all' || aptStaff === staffFilter;
        const matchesStatus = statusFilter === 'all' || apt.status === statusFilter;
        return matchesSearch && matchesStaff && matchesStatus;
    }), [appointments, search, staffFilter, statusFilter, viewAllStaff, ownStaff]);

    const summary = useMemo(() => {
        const s = { total: appointments.length, value: 0 };
        Object.keys(STATUS_META).forEach((k) => { s[k] = 0; });
        appointments.forEach((a) => {
            s[a.status] = (s[a.status] || 0) + 1;
            if (!['Cancelled', 'No_Show'].includes(a.status)) s.value += Number(a.finalAmount ?? a.amount) || 0;
        });
        return s;
    }, [appointments]);

    const currentAppointment = formData.id ? appointments.find((a) => a._id === formData.id) || null : null;

    /* ============================== render ============================== */
    if (!storeId || storeId === 'global_hq') {
        return (
            <main className={styles.page}>
                <AppointmentHeader isFetching={false} onRefresh={() => {}} onNewAppointment={() => {}} canCreate={false} />
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--h-color-neutral-500, #797980)', fontSize: '14px', fontWeight: 700 }}>
                    Select a branch from the top menu to see or book appointments.
                </div>
            </main>
        );
    }

    const shareText = shareBar && {
        NEW: { title: 'Appointment booked', sub: `${shareBar.appointment.clientName}, ${prettyDate(shareBar.appointment.date)} at ${prettyTime(shareBar.appointment.startTime)}` },
        RESCHEDULED: { title: 'Appointment moved', sub: `New time: ${prettyDate(shareBar.appointment.date)} at ${prettyTime(shareBar.appointment.startTime)}` },
        UPDATED: { title: 'Appointment updated', sub: shareBar.appointment.clientName },
        CANCELLED: { title: 'Appointment cancelled', sub: `${shareBar.appointment.clientName}, ${prettyTime(shareBar.appointment.startTime)}` },
        COMPLETED: { title: 'Marked as completed', sub: `${shareBar.appointment.clientName}. Send a thank-you and ask for feedback.` },
    }[shareBar.kind];

    return (
        <main className={styles.page}>
            <AppointmentHeader isFetching={isFetching} onRefresh={fetchMasterData} onNewAppointment={() => openNewAppointment()} canCreate={canCreate} />

            <AppointmentToolbar
                selectedDate={selectedDate} onChangeDate={changeDate} setToday={() => setSelectedDate(today)}
                search={search} onSearch={setSearch} staffFilter={staffFilter} onFilter={setStaffFilter} staffList={visibleStaff}
                statusFilter={statusFilter} onStatusFilter={setStatusFilter}
            />

            {/* Day summary (click to filter) */}
            <div className={ex.summary}>
                <button type="button" className={`${ex.chip} ${statusFilter === 'all' ? ex.chipActive : ''}`} onClick={() => setStatusFilter('all')}>All <b>{summary.total}</b></button>
                {Object.entries(STATUS_META).map(([key, meta]) => (summary[key] > 0 || key === 'Confirmed' ? (
                    <button type="button" key={key} className={`${ex.chip} ${statusFilter === key ? ex.chipActive : ''}`} onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}>
                        <i style={{ background: meta.color }} /> {meta.label} <b>{summary[key]}</b>
                    </button>
                ) : null))}
                <span className={`${ex.chip} ${ex.chipValue}`}>Booked value <b>{inr(summary.value)}</b></span>
            </div>

            {toast && <div className={`${ex.toast} ${toast.type === 'ok' ? ex.toastOk : ''}`} role="status">{toast.text}</div>}

            {/* After saving: send it on WhatsApp */}
            {shareBar && shareText && (
                <div className={ex.shareBar} role="status">
                    <div className={ex.shareBarText}><strong>{shareText.title}</strong><span>{shareText.sub}</span></div>
                    {canWhatsApp && shareBar.kind === 'NEW' && (
                        <button type="button" className={ex.waBtn} onClick={() => sendWhatsApp({ appointment: shareBar.appointment, type: 'CONFIRMED', audience: 'CLIENT' })}><MessageCircle size={13} /> Confirm to client</button>
                    )}
                    {canWhatsApp && shareBar.kind === 'RESCHEDULED' && (
                        <button type="button" className={ex.waBtn} onClick={() => sendWhatsApp({ appointment: shareBar.appointment, type: 'RESCHEDULED', audience: 'CLIENT' })}><MessageCircle size={13} /> Send new time to client</button>
                    )}
                    {canStaffMsg && ['NEW', 'RESCHEDULED'].includes(shareBar.kind) && (
                        <button type="button" className={`${ex.waBtn} ${ex.waBtnGhost}`} onClick={() => sendWhatsApp({ appointment: shareBar.appointment, type: 'STAFF_NEW', audience: 'STAFF' })}><MessageCircle size={13} /> Tell staff</button>
                    )}
                    {canWhatsApp && shareBar.kind === 'UPDATED' && (
                        <button type="button" className={`${ex.waBtn} ${ex.waBtnGhost}`} onClick={() => sendWhatsApp({ appointment: shareBar.appointment, type: 'CONFIRMED', audience: 'CLIENT' })}><MessageCircle size={13} /> Send details to client</button>
                    )}
                    {canWhatsApp && shareBar.kind === 'CANCELLED' && (
                        <button type="button" className={ex.waBtn} onClick={() => sendWhatsApp({ appointment: shareBar.appointment, type: 'CANCELLED', audience: 'CLIENT' })}><MessageCircle size={13} /> Tell client</button>
                    )}
                    {canWhatsApp && shareBar.kind === 'COMPLETED' && (
                        <button type="button" className={ex.waBtn} onClick={() => sendWhatsApp({ appointment: shareBar.appointment, type: 'THANK_YOU', audience: 'CLIENT' })}><MessageCircle size={13} /> Send thank-you</button>
                    )}
                    <button type="button" className={ex.closeBar} onClick={() => setShareBar(null)} aria-label="Close"><X size={14} /></button>
                </div>
            )}

            <section className={styles.workspace}>
                {isFetching && staffList.length === 0 ? (
                    <div className={styles.loadingState}><Loader2 size={24} className={styles.spin} /> Loading schedule...</div>
                ) : !viewAllStaff && !ownStaff ? (
                    <div className={styles.loadingState} style={{ textAlign: 'center', lineHeight: 1.6 }}>
                        You can see only your own bookings, but this login is not linked to a staff profile.<br />Ask your manager to link it in Staff.
                    </div>
                ) : (
                    <AppointmentCalendar
                        staffList={staffFilter === 'all' ? visibleStaff : visibleStaff.filter((s) => String(s._id) === staffFilter)}
                        visibleAppointments={visibleAppointments}
                        onEmptySlotClick={openNewAppointment}
                        onAppointmentClick={openEditAppointment}
                        canCreate={canCreate}
                        canEdit={canOpen}
                        selectedDate={selectedDate}
                        isToday={selectedDate === today}
                        onShareSchedule={canStaffMsg ? shareStaffSchedule : null}
                    />
                )}
            </section>

            <AppointmentDrawer
                isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} formData={formData} setFormData={setFormData}
                onSubmit={handleSubmit} isSubmitting={isSubmitting} errorMsg={errorMsg} staffList={staffList} serviceList={serviceList}
                onServiceChange={handleServiceChange} onStartChange={handleStartChange}
                clientQuery={formData.clientName} onClientSearch={handleClientSearchInput}
                clientResults={clientResults} showClientDropdown={showClientDropdown} setShowClientDropdown={setShowClientDropdown} selectClient={selectClient}
                canDelete={canCancel} canEdit={canEdit} canReschedule={canReschedule} canChangeStatus={canChangeStatus}
                canWhatsApp={canWhatsApp} canStaffMsg={canStaffMsg}
                appointment={currentAppointment}
                onQuickStatus={handleQuickStatus}
                onSendWhatsApp={(type, audience) => currentAppointment && sendWhatsApp({ appointment: currentAppointment, type, audience })}
                statusMeta={STATUS_META}
            />
        </main>
    );
}